import React, { useState, useEffect } from 'react';
import { X, Loader2, Trash2, Save, AlertTriangle, Plane, ShieldCheck } from 'lucide-react';
import { updateEvent, deleteEvent, sendMessage, EVENT_TYPES, APPROVAL_STATUS } from '../services/api';
import { appendSentMessage } from '../lib/sentMessagesCache';
import { timeZoneForWorker, dayKeyInZone, formatHmInZone, zonedDateTimeToInstant } from '../lib/workerTime';
import { useToast } from './Toast';
import { FEEDBACK, isPendingApproval } from '../services/feedbackMessages';

// Edita un periodo de vacaciones ya existente del propio trabajador. Permite:
//   - Cambiar fecha inicio y/o fin. Si estaba APROBADO, vuelve a PENDIENTE.
//   - Solicitar eliminar el periodo entero. Pasa también por aprobación: el
//     admin decide si DELETE de verdad o vuelve a APROBADO.
//
// La "petición de eliminación" se marca con un sufijo en geoInfo del evento
// START (`;reqdelete=1`) — mismo workaround que ya usamos para `;extra=1`
// en compensaciones. AprobacionesPage detecta ese flag y al aprobar borra
// los dos eventos; al rechazar quita el flag y deja el periodo aprobado.

const REQDELETE_FLAG = ';reqdelete=1';

// Backend usa timestamp en UTC. Al cambiar de día conservamos la hora de
// reloj que ya tenía el evento (00:00 / 23:59 en la zona del TRABAJADOR) para
// no introducir un cambio de hora colateral. Fecha y hora se interpretan en
// la zona del trabajador, nunca en la del navegador.
const dayToIsoUtc = (dayIso, hm, tz) => zonedDateTimeToInstant(dayIso, hm || '00:00', tz).toISOString();

// Tag de auditoría que dejamos en geoInfo cuando un admin edita o borra
// un periodo. Como el backend no tiene un campo dedicado de "modificado
// por", usamos el mismo workaround de geoInfo:
//   ;modby=<email>:<isoUtcMinutos>
// Conservamos un único modby a la vez — si hay otro previo, lo
// sobrescribimos para no acumular ruido.
const buildAuditTag = (adminUser) => {
  if (!adminUser) return null;
  const who = adminUser.email || adminUser.name || `id${adminUser.id}`;
  // Minutos UTC bastan para identificar la acción sin meter caracteres
  // problemáticos. Quitamos los ":" del email solo por si los hubiera.
  const when = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  return `;modby=${String(who).replace(/[:;]/g, '_')}:${when}`;
};

const stripAuditAndDeleteFlags = (raw) =>
  String(raw || '').replace(/;modby=[^;]*/g, '').replace(/;reqdelete=\d/g, '');

const VacationEditModal = ({
  open,
  period,
  onClose,
  onSubmitted,
  // Modo admin: el viewer NO es el dueño del periodo. Aplica cambios sin
  // pasar por aprobación y deja registro de quién lo hizo en geoInfo.
  // Requiere `adminUser` (el viewer) y `targetWorker` (el dueño del periodo)
  // para notificar al trabajador cuando se elimine.
  adminMode = false,
  adminUser = null,
  targetWorker = null,
  // Zona del DUEÑO del periodo. Si no viene, se deriva de `targetWorker`
  // (modo admin); el calendario propio la pasa explícitamente.
  timeZone = null,
}) => {
  const tz = timeZone || timeZoneForWorker(targetWorker);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (open && period) {
      setStart(dayKeyInZone(period.start, tz) || '');
      setEnd(dayKeyInZone(period.end, tz) || '');
      setSubmitting(false);
      setConfirmDelete(false);
      setError(null);
    }
  }, [open, period, tz]);

  if (!open || !period) return null;

  const wasApproved = period.approvalStatus === APPROVAL_STATUS.APPROVED;
  const hasPendingDelete =
    String(period.startEvent?.geoInfo || '').includes('reqdelete=1');

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!start || !end) { setError('Indica fecha de inicio y de fin.'); return; }
    if (end < start) { setError('La fecha de fin no puede ser anterior a la de inicio.'); return; }
    setSubmitting(true);
    try {
      // Tag de auditoría — solo en modo admin. En modo trabajador no
      // queremos contaminar el geoInfo (es su propia edición).
      const auditTag = adminMode ? buildAuditTag(adminUser) : '';
      // OJO: el backend hace `geoInfo || actual`, así que null y '' NO borran
      // nada (el `;reqdelete=1` se quedaba y el periodo seguía "pendiente de
      // eliminación" tras editarlo). Si al limpiar no queda nada, se envía
      // la marca explícita `;reqdelete=0` (ver stripDeleteRequestFlag).
      const buildGeo = (raw) => {
        const cleaned = stripAuditAndDeleteFlags(raw);
        const next = cleaned + (auditTag || '');
        if (next) return next;
        return String(raw || '') ? ';reqdelete=0' : null;
      };
      // En modo admin la edición se aplica directamente — no baja a
      // PENDING. En modo trabajador, sí debe pasar por aprobación.
      const nextStatus = adminMode ? APPROVAL_STATUS.APPROVED : APPROVAL_STATUS.PENDING;
      await updateEvent(period.startEvent.id, {
        workerId: period.startEvent.workerId,
        eventType: EVENT_TYPES.VACATION_START,
        timestamp: dayToIsoUtc(start, formatHmInZone(period.startEvent.timestamp, tz) || '00:00', tz),
        geoInfo: buildGeo(period.startEvent?.geoInfo),
        approvalStatus: nextStatus,
      });
      await updateEvent(period.endEvent.id, {
        workerId: period.endEvent.workerId,
        eventType: EVENT_TYPES.VACATION_END,
        timestamp: dayToIsoUtc(end, formatHmInZone(period.endEvent.timestamp, tz) || '23:59', tz),
        geoInfo: buildGeo(period.endEvent?.geoInfo),
        approvalStatus: nextStatus,
      });
      toast.success(FEEDBACK.vacationEdited({
        start, end, pending: isPendingApproval(nextStatus), workerName: adminMode ? targetWorker?.name : null,
      }));
      // En modo admin, notificamos al trabajador del cambio. Si el aviso
      // falla (p.ej. 403 del endpoint /api/message) la edición ya está
      // aplicada en BD y el log queda en geoInfo (modby=...): solo se avisa.
      if (adminMode && adminUser?.id && targetWorker?.id) {
        try {
          const who = adminUser.name || adminUser.email || `Admin #${adminUser.id}`;
          const created = await sendMessage({
            senderId: adminUser.id,
            receiverId: targetWorker.id,
            content: `${who} ha modificado tu periodo de vacaciones. Nuevas fechas: del ${start} al ${end}.`,
          });
          // Trackeamos el mensaje en la caché de "enviados" del admin para
          // que MessagingHub lo muestre en su chat con el trabajador.
          appendSentMessage(adminUser.id, created);
        } catch {
          toast.warning(FEEDBACK.workerNotNotified);
        }
      }
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la edición.');
    } finally {
      setSubmitting(false);
    }
  };

  // Acción exclusiva del modo admin: borra los dos extremos del periodo y
  // notifica al trabajador con un mensaje automático que deja constancia
  // de quién lo eliminó y cuándo (audit trail mínimo sin tocar BD).
  const handleAdminDelete = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const startIso = dayKeyInZone(period.start, tz);
      const endIso = dayKeyInZone(period.end, tz);
      await deleteEvent(period.startEvent.id);
      await deleteEvent(period.endEvent.id);
      toast.success(FEEDBACK.vacationDeleted({ start: startIso, end: endIso, workerName: targetWorker?.name }));
      // Notificación al trabajador. No bloqueamos el flujo si falla — el
      // borrado ya está hecho; el mensaje es informativo.
      try {
        if (adminUser?.id && targetWorker?.id) {
          const who = adminUser.name || adminUser.email || `Admin #${adminUser.id}`;
          const created = await sendMessage({
            senderId: adminUser.id,
            receiverId: targetWorker.id,
            content: `${who} ha eliminado tu periodo de vacaciones del ${startIso} al ${endIso}.`,
          });
          appendSentMessage(adminUser.id, created);
        }
      } catch {
        // El delete ya está aplicado y queda en el log de operaciones del
        // admin; solo avisamos de que el trabajador no recibió el mensaje.
        toast.warning(FEEDBACK.workerNotNotified);
      }
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'No se pudo eliminar el periodo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestDelete = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const tagGeo = (raw) => {
        const cleaned = String(raw || '').replace(/;reqdelete=\d/g, '');
        return cleaned + REQDELETE_FLAG;
      };
      await updateEvent(period.startEvent.id, {
        workerId: period.startEvent.workerId,
        eventType: EVENT_TYPES.VACATION_START,
        timestamp: period.startEvent.timestamp,
        geoInfo: tagGeo(period.startEvent.geoInfo),
        approvalStatus: APPROVAL_STATUS.PENDING,
      });
      await updateEvent(period.endEvent.id, {
        workerId: period.endEvent.workerId,
        eventType: EVENT_TYPES.VACATION_END,
        timestamp: period.endEvent.timestamp,
        geoInfo: tagGeo(period.endEvent.geoInfo),
        approvalStatus: APPROVAL_STATUS.PENDING,
      });
      toast.success(FEEDBACK.vacationDeleteRequested({
        start: dayKeyInZone(period.start, tz), end: dayKeyInZone(period.end, tz),
      }));
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'No se pudo solicitar la eliminación.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Plane size={16} className="text-accent-dark" />
            <div>
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">
                {adminMode ? 'Editar como admin' : 'Editar'}
              </div>
              <h2 className="font-display text-lg font-bold text-ink">
                {adminMode && targetWorker?.name
                  ? `Vacaciones de ${targetWorker.name}`
                  : 'Periodo de vacaciones'}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSaveEdit} className="px-6 py-5 space-y-4">
          {adminMode && (
            <div className="text-xs text-primary-darker bg-primary-soft/50 border border-primary/20 rounded-lg px-3 py-2 inline-flex items-start gap-2">
              <ShieldCheck size={13} className="shrink-0 mt-0.5" />
              <span>
                Editas como administrador. Los cambios se aplicarán <strong>directamente</strong> sin pasar por aprobación.
                Quedará registrado quién hizo la modificación.
              </span>
            </div>
          )}
          {hasPendingDelete && !adminMode && (
            <div className="text-xs text-warning bg-warning/5 border border-warning/20 rounded-lg px-3 py-2 inline-flex items-start gap-2">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <span>Este periodo tiene una solicitud de eliminación pendiente de revisión por el admin. Si editas las fechas, se cancelará la eliminación.</span>
            </div>
          )}
          {wasApproved && !hasPendingDelete && !adminMode && (
            <div className="text-xs text-ink-muted bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              Este periodo estaba <span className="font-semibold text-ink">aprobado</span>. Cualquier cambio
              lo dejará pendiente de aprobación de nuevo.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Desde</label>
              <input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Hasta</label>
              <input
                type="date"
                value={end}
                min={start}
                onChange={(e) => setEnd(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                required
              />
            </div>
          </div>

          {error && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2">{error}</div>
          )}

          {!confirmDelete ? (
            <div className="flex items-center justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={submitting || (hasPendingDelete && !adminMode)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-danger hover:bg-danger/10 transition-colors disabled:opacity-50"
                title={
                  adminMode ? 'Eliminar definitivamente este periodo'
                  : hasPendingDelete ? 'Ya hay una solicitud de eliminación pendiente'
                  : 'Solicitar eliminación de este periodo'
                }
              >
                <Trash2 size={14} /> {adminMode ? 'Eliminar' : 'Solicitar eliminación'}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-60"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  <Save size={14} /> Guardar cambios
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-danger/30 bg-danger/5 p-3 space-y-3">
              <div className="flex items-start gap-2 text-sm text-danger">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {adminMode ? (
                    <>
                      Vas a <strong>eliminar definitivamente</strong> este periodo de vacaciones
                      {targetWorker?.name ? <> de <strong>{targetWorker.name}</strong></> : null}.
                      Se notificará al trabajador. ¿Continuar?
                    </>
                  ) : (
                    <>
                      Vas a solicitar al admin que <strong>elimine este periodo</strong> de vacaciones.
                      No se borra hasta que el admin lo apruebe. ¿Continuar?
                    </>
                  )}
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  disabled={submitting}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-ink-muted hover:bg-slate-100 transition-colors"
                >
                  Volver
                </button>
                <button
                  type="button"
                  onClick={adminMode ? handleAdminDelete : handleRequestDelete}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-danger text-white hover:bg-red-700 transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  {adminMode ? 'Eliminar definitivamente' : 'Confirmar solicitud'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default VacationEditModal;
